// Owner: account-settings (plan §9.3). Session guard of the authenticated layouts, own preferences
// (language, time zone, formats, theme, start page) applied the moment they change, and sign-out.
import { redirect } from '@tanstack/react-router';
import { isApiError } from '@dataroom/api-client';
import type { MeDTO, StartPage, UpdateMemberPreferencesInput } from '@dataroom/core';
import {
  logout,
  updateMyPreferences as updatePreferencesContract,
} from '@dataroom/contracts/account/session';
import { applyTheme, storedTheme } from '../features/account/theme';
import { api } from './api';
import { applyFormats, localeReady } from './i18n';
import { meQuery, qk, queryClient } from './queries';

// The remembered theme paints before `/me` arrives (no flash of the other scheme after a reload).
if (typeof document !== 'undefined') applyTheme(storedTheme());

const LAST_PATH_KEY = 'dr.lastPath';
/** The start page applies once per page load: opening the app at `/`, never on later navigation. */
let startPageChecked = false;

function rememberPath(href: string): void {
  try {
    localStorage.setItem(LAST_PATH_KEY, href);
  } catch {
    // Storage unavailable: "last page" falls back to Home.
  }
}

function lastPath(): string | null {
  try {
    const v = localStorage.getItem(LAST_PATH_KEY);
    return v && v.startsWith('/') && !v.startsWith('//') ? v : null;
  } catch {
    return null;
  }
}

/** Where the app opens for a start page preference (null: stay on Home). */
export function startPath(start: StartPage, last: string | null = lastPath()): string | null {
  switch (start) {
    case 'inbox':
      return '/inbox';
    case 'library':
      return '/library';
    case 'last':
      return last && last !== '/' ? last : null;
    default:
      return null;
  }
}

/**
 * `beforeLoad` guard for authenticated layouts: loads /me (cached) or redirects to /login with
 * the current location as `redirect`. Applies the member's language, time zone, formats and theme
 * before the page renders, and opens the member's start page when the app starts at `/`.
 */
export async function requireSession(location: { href: string; pathname?: string }): Promise<MeDTO> {
  let me: MeDTO;
  try {
    me = await queryClient.ensureQueryData(meQuery);
  } catch (err) {
    if (isApiError(err) && err.status === 401) {
      throw redirect({ to: '/login', search: { redirect: location.href } });
    }
    throw err;
  }
  applyFormats(me.formats);
  applyTheme(me.preferences.theme ?? 'system');
  // Messages of another language load on demand: the first screen renders in the member's.
  await localeReady();
  const pathname = location.pathname ?? location.href.split(/[?#]/)[0];
  if (!startPageChecked) {
    startPageChecked = true;
    const target = pathname === '/' ? startPath(me.preferences.startPage ?? 'home') : null;
    if (target && target !== location.href) throw redirect({ href: target, replace: true });
  }
  if (pathname !== '/login') rememberPath(location.href);
  return me;
}

/**
 * Reload /me after a change of language, time zone or formats (own preferences or the
 * workspace defaults). A new language needs a fresh render of every screen, so the page reloads.
 */
export async function refreshFormats(): Promise<MeDTO> {
  const me = await queryClient.fetchQuery({ ...meQuery, staleTime: 0 });
  if (applyFormats(me.formats)) window.location.reload();
  return me;
}

/**
 * Set your own preferences (null follows the workspace default) and apply them at once: formats
 * and time zone re-render every value, the theme repaints, and a new language reloads the page so
 * every screen renders in it.
 */
export async function updateMyPreferences(input: UpdateMemberPreferencesInput): Promise<MeDTO> {
  const me = await api.call(updatePreferencesContract, { body: input });
  queryClient.setQueryData(qk.me, me);
  applyTheme(me.preferences.theme ?? 'system');
  if (applyFormats(me.formats)) window.location.reload();
  else void queryClient.invalidateQueries({ predicate: (q) => q.queryKey[0] !== 'me' });
  return me;
}

/** End this session and go to the sign-in page. */
export async function signOut(): Promise<void> {
  try {
    await api.call(logout);
  } finally {
    queryClient.clear();
    window.location.assign('/login');
  }
}
